import type { ApiClient, Schemas } from '@argzombie/api-client';
import { ok } from '../../lib/api';

/**
 * The player Quick actions (card #250): what each button is, the confirm text its dialog shows (built from
 * `GET /admin/players/{id}/quick-actions`, so it says exactly what will happen to this player), and the calls. The API
 * (`Features/Admin/AdminQuickActionEndpoints`) needs Admin + MFA and a reason, and writes one `admin.quick.<action>`
 * audit entry per press.
 */
export type QuickContext = Schemas['QuickActionsContext'];
export type QuickResult = Schemas['QuickActionResult'];
export type QuickActionId = 'patch-up' | 'supply-drop' | 'homebase-reset' | 'base-resupply';

/** The knobs a dialog can change; null = the server default (shown in the dialog). */
export type QuickKnobs = {
  days: number;
  magazines: number;
  multiplier: number;
  camps: boolean;
  home: boolean;
  facilities: boolean;
  buildOrders: boolean;
  inventory: boolean;
};

export const QUICK_ACTIONS: { id: QuickActionId; label: string; subtitle: string }[] = [
  { id: 'patch-up', label: 'Rest up', subtitle: 'Resurrect + fully rested, no ailments' },
  { id: 'supply-drop', label: 'Supply drop', subtitle: 'Armed and fed for a month' },
  { id: 'homebase-reset', label: 'Homebase reset', subtitle: 'Fix a broken home or outposts' },
  { id: 'base-resupply', label: 'Base resupply', subtitle: 'Materials to craft anything' },
];

/** The dialog's starting knobs: the server's defaults, and the homebase reset's default boxes (camps + home). */
export function defaultKnobs(ctx: QuickContext | undefined): QuickKnobs {
  return {
    days: ctx?.defaults.supplyDays ?? 30,
    magazines: ctx?.defaults.ammoMagazinesPerDay ?? 2,
    multiplier: ctx?.defaults.resupplyMultiplier ?? 10,
    camps: true,
    home: true,
    facilities: false,
    buildOrders: false,
    inventory: false,
  };
}

/** The run is a zombie (ADR 0069; the API's older word is `dead`). */
const dead = (ctx: QuickContext) => ctx.runStatus === 'dead' || ctx.runStatus === 'zombie';
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;

/**
 * What pressing confirm will do, line by line, for this player and these knobs. `blocked` is set when the server will
 * refuse (then the confirm button is off and the line says why). `name` is the selected character's (ADR 0069: every
 * action acts on that character's run).
 */
export function describe(
  id: QuickActionId,
  ctx: QuickContext,
  k: QuickKnobs,
  name = 'the character',
): { lines: string[]; never: string; blocked?: string } {
  switch (id) {
    case 'patch-up': {
      const lines: string[] = [];
      if (dead(ctx)) {
        if (!ctx.canRevive)
          return {
            lines: [],
            never: '',
            blocked: ctx.reviveBlocker ?? `${cap(name)} cannot be resurrected.`,
          };
        lines.push(
          `Resurrect ${name} (the same as the player's paid un-zombie, plus a full heal): run ${ctx.runNumber ?? '?'} comes back alive in place, at home, with the teams, recruits, base and orders that froze with it, and its clocks resume from now. It never starts a new character, and no resurrection credit is spent.`,
          'Not restored: the raid that was open at death (lost with its loot) and the effects death ended.',
        );
      }
      lines.push(
        `Rest the player and every recruit (${plural(ctx.members, 'member')}): no rest debt left (everyone starts FRESH), and the old health column full.`,
        'Clear every condition (wounded, laid up, sick, bitten), release the infirmary beds, zero the starvation streak and end debuff effects such as Hungry and Starving.',
        'Repair every armor piece the run owns (worn or in the pool): its wear is cleared.',
      );
      return { lines, never: 'Never touches inventory, facilities, bases or account progress.' };
    }
    case 'supply-drop': {
      if (dead(ctx))
        return { lines: [], never: '', blocked: `${cap(name)} is a zombie: use Rest up first.` };
      const weapon = ctx.weaponPick
        ? `${ctx.weaponPick.name} (${ctx.weaponPick.rule})`
        : 'none in content';
      const armor = ctx.armorPick
        ? `${ctx.armorPick.name} (${ctx.armorPick.rule})`
        : 'none in content';
      return {
        lines: [
          `For ${name}: a weapon for every team member the run's stock doesn't already cover: ${weapon}.`,
          `Armor the same way: ${armor}.`,
          `Ammo for every ranged weapon the team carries: magazine × ${k.magazines} a day × ${k.days} days.`,
          `Food and water for everyone who eats at upkeep, for ${k.days} days (the content's daily rations).`,
          'Weapons, armor and ammo go into the shared goods pool (Inventory), so any member can equip them; the armor arrives unworn. Food and water go into Inventory. It tops off and never stacks: pressing again right after changes nothing. Weights are reported.',
        ],
        never:
          'Never takes anything away, and never touches facilities, bases or account progress.',
      };
    }
    case 'homebase-reset': {
      const lines: string[] = [
        `Acts on ${name}'s bases only: homes and outposts belong to each character, so the player's other characters keep theirs.`,
      ];
      if (k.camps)
        lines.push(
          `End ${plural(ctx.activeCamps, 'active outpost')} (revoked; their zones go inactive).`,
        );
      if (k.home)
        lines.push(
          ctx.hasHome
            ? 'End the home and clear the home location: the player sets a new home the next time they open the game.'
            : 'Clear the home location (no active home now).',
        );
      if (k.facilities)
        lines.push(
          `Remove ${plural(ctx.facilities, 'built facility', 'built facilities')} of this run (facility build orders cancelled, infirmary beds emptied).`,
        );
      if (k.buildOrders)
        lines.push(
          `Cancel ${plural(ctx.activeBuildOrders, 'running build order')} (made parts stay).`,
        );
      if (k.inventory)
        lines.push(
          `Empty Inventory (${plural(ctx.baseItemUnits, 'item')}), and unequip anything that was in it.`,
        );
      if (lines.length === 1)
        return { lines: [], never: '', blocked: 'Tick at least one thing to reset.' };
      return {
        lines,
        never:
          'Never touches account tables: blueprints, the mastery ledger, the stat sheet, story progress, trophies.',
      };
    }
    case 'base-resupply':
      if (dead(ctx))
        return { lines: [], never: '', blocked: `${cap(name)} is a zombie: use Rest up first.` };
      return {
        lines: [
          `For ${name}: every craft input (raw materials, intermediates, parts) topped to the most any single recipe or facility level needs × ${k.multiplier}.`,
          'One of each tool a recipe requires. Key parts are left out (they come from hunts).',
          'Delivered into Inventory. Pressing again tops off to the same targets, so it never stacks.',
        ],
        never: 'Never takes anything away.',
      };
  }
}

/**
 * Calls the action on the run `runId` (ADR 0069; null = the active character). The run id goes in the body, and the
 * run-scoped client (`withRunId`) also adds `?runId=`.
 */
export async function runQuickAction(
  client: ApiClient,
  playerId: string,
  id: QuickActionId,
  k: QuickKnobs,
  reason: string,
  runId?: string | null,
): Promise<QuickResult> {
  const params = { path: { id: playerId } };
  const run = runId || undefined;
  switch (id) {
    case 'patch-up':
      return ok(
        await client.POST('/admin/players/{id}/quick/patch-up', {
          params,
          body: { reason, runId: run },
        }),
      );
    case 'supply-drop':
      return ok(
        await client.POST('/admin/players/{id}/quick/supply-drop', {
          params,
          body: { reason, runId: run, days: k.days, ammoMagazinesPerDay: k.magazines },
        }),
      );
    case 'homebase-reset':
      return ok(
        await client.POST('/admin/players/{id}/quick/homebase-reset', {
          params,
          body: {
            reason,
            runId: run,
            camps: k.camps,
            home: k.home,
            facilities: k.facilities,
            buildOrders: k.buildOrders,
            inventory: k.inventory,
          },
        }),
      );
    case 'base-resupply':
      return ok(
        await client.POST('/admin/players/{id}/quick/base-resupply', {
          params,
          body: { reason, runId: run, multiplier: k.multiplier },
        }),
      );
  }
}
